import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDateTime, getStatusColor, getSubStatusColor, validateOrderCalculations, calculateOrderTotal } from '../utils/helpers';
import { getClients, getWholesalerById, getClientById, updateManagementOrderStatus, testManagementAccess, updateOrderDetails, uploadOrderAttachments, getOrderAttachments, updateScratchOrder, getManagementOrderById, splitScratchOrder } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { debugToken } from '../services/auth';
import Modal from '../components/common/Modal';
import SplitModal from '../components/common/SplitModal';
import EditScratchOrdersModal from '../components/common/EditScratchOrdersModal';

const formatNumber = (num) => Number(num).toLocaleString('en-US');
// Sanitize note input to mitigate injection/XSS and limit length
const sanitizeNote = (text) => {
    if (typeof text !== 'string') return '';
    const withoutControl = text.replace(/[\u0000-\u001F\u007F]/g, '');
    const withoutTags = withoutControl.replace(/[<>`]/g, '');
    return withoutTags.slice(0, 250);
};

// Format file sizes: show KB if < 1 MB, otherwise MB
const formatFileSize = (bytes) => {
    if (typeof bytes !== 'number' || Number.isNaN(bytes)) return '';
    const oneMB = 1024 * 1024;
    if (bytes < oneMB) {
        return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${(bytes / oneMB).toFixed(2)} MB`;
};

const warehouseMap = {
    EBL_WH: 'Erbil Warehouse',
    DUH_WH: 'Dohuk Warehouse',
    SUL_WH: 'Suly Warehouse',
    KRK_WH: 'Kirkuk Warehouse',
    MSL_WH: 'Mosul Warehouse',
    BGD_WH: 'Baghdad Warehouse',
};

// Helper to fetch client name by ID if not in clients list
const useClientName = (clientId) => {
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        let isMounted = true;
        if (!clientId) return;
        setLoading(true);
        setError('');
        getClientById(clientId)
            .then(data => { if (isMounted) setName(data.name || ''); })
            .catch(() => { if (isMounted) setError('Could not fetch client name'); })
            .finally(() => { if (isMounted) setLoading(false); });
        return () => { isMounted = false; };
    }, [clientId]);
    return { name, loading, error };
};

// Separate component for E-PIN client group
const EpinClientGroup = ({ clientId, epins, clients }) => {
    const client = clients.find(c => c.id === clientId);
    const { name: fetchedName, loading: nameLoading, error: nameError } = useClientName(client ? null : clientId);

    // Sort epins by denomination value (smallest to largest)
    const sortedEpins = [...epins].sort((a, b) => a.denomination - b.denomination);

    return (
        <div className="mb-4">
            <div className="font-bold text-blue-900 text-sm mb-2">
                {client ? client.name : nameLoading ? 'Loading client name...' : nameError ? nameError : fetchedName || 'Unknown Client'}
            </div>
            <div className="overflow-x-auto w-full border border-gray-200 rounded-lg mb-2 shadow-sm">
                <table className="min-w-full text-sm divide-y divide-gray-200">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Denomination</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sortedEpins.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 font-medium tabular-nums">IQD {formatNumber(item.denomination)}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">IQD {formatNumber(item.denominationPrice)}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">{item.quantity}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 font-semibold tabular-nums">IQD {formatNumber(item.paidAmount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ManagementOrderDetailsView = ({ order, onOrderUpdate, pageType }) => {
    const { user } = useAuth();
    const isEmployee = user?.role === 'KTE-Employees' || user?.accountType === 'KTE-Employees';
    // Validate backend totals against frontend calculations
    useEffect(() => {
        if (order) {
            const validation = validateOrderCalculations(order);

            if (!validation.isValid) {
                console.warn('Total calculation discrepancy detected:', {
                    orderId: order.id,
                    validation,
                    order
                });
            } else {
                console.log('Order calculations validated successfully:', {
                    orderId: order.id,
                    total: validation.total
                });
            }
        }
    }, [order]);

    // Test management access on component mount
    useEffect(() => {
        const testAccess = async () => {
            await testManagementAccess();
            debugToken();
        };
        testAccess();
    }, []);
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(true);
    const { accessToken } = useAuth();
    const [merchantName, setMerchantName] = useState(order.merchantName || '');
    const [merchantLoading, setMerchantLoading] = useState(false);
    const [merchantError, setMerchantError] = useState('');
    const [clientName, setClientName] = useState('');
    const [clientNameLoading, setClientNameLoading] = useState(false);
    const [clientNameError, setClientNameError] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [attachmentError, setAttachmentError] = useState('');
    const [attachmentSuccess, setAttachmentSuccess] = useState('');
    const [processingFiles, setProcessingFiles] = useState(false);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [existingAttachmentsLoading, setExistingAttachmentsLoading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadingAttachments, setUploadingAttachments] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    // Centralized scratch order editing state
    const [updatingScratchOrders, setUpdatingScratchOrders] = useState(false);
    const [scratchOrdersError, setScratchOrdersError] = useState('');
    const [scratchOrdersSuccess, setScratchOrdersSuccess] = useState('');
    const [refreshingOrder, setRefreshingOrder] = useState(false);
    const [selectedScratchOrderForEdit, setSelectedScratchOrderForEdit] = useState(null);

    // Split modal state
    const [showSplitModal, setShowSplitModal] = useState(false);
    const [selectedScratchOrderForSplit, setSelectedScratchOrderForSplit] = useState(null);
    const [splittingOrder, setSplittingOrder] = useState(false);

    const handleFilesAdd = (incomingFiles) => {
        setAttachmentError('');
        setAttachmentSuccess('');
        setProcessingFiles(true);
        const files = Array.from(incomingFiles || []);
        const allowed = ['image/png', 'image/jpeg', 'application/pdf', 'application/zip', 'application/x-zip-compressed'];
        const maxSize = 20 * 1024 * 1024; // 20 MB in bytes

        // Filter files by type and size
        const valid = files.filter((f) => {
            const isValidType = allowed.includes(f.type);
            const isValidSize = f.size <= maxSize;
            return isValidType && isValidSize;
        });

        // Check for type errors
        const invalidTypeFiles = files.filter((f) => !allowed.includes(f.type));
        if (invalidTypeFiles.length > 0) {
            const fileNames = invalidTypeFiles.map(f => f.name).join(', ');
            setAttachmentError(`Unsupported file type(s): ${fileNames}. Only PNG, JPEG, PDF, and ZIP files are allowed.`);
        }

        // Check for size errors
        const oversizedFiles = files.filter((f) => f.size > maxSize);
        if (oversizedFiles.length > 0) {
            const fileNames = oversizedFiles.map(f => f.name).join(', ');
            const sizes = oversizedFiles.map(f => (f.size / (1024 * 1024)).toFixed(2)).join(', ');
            setAttachmentError(`Files exceeding 20 MB limit: ${fileNames} (${sizes} MB). Please compress or resize these files.`);
        }

        if (valid.length > 0) {
            setAttachments((prev) => [...prev, ...valid]);
            // Show success message for valid files
            if (valid.length === 1) {
                setAttachmentSuccess(`${valid[0].name} added successfully`);
            } else if (valid.length > 1) {
                setAttachmentSuccess(`${valid.length} files added successfully`);
            }
            // Clear success message after 3 seconds
            setTimeout(() => setAttachmentSuccess(''), 3000);
        }
        setProcessingFiles(false);
    };

    const handleViewFile = (file) => {
        try {
            const url = URL.createObjectURL(file);
            window.open(url, '_blank', 'noopener,noreferrer');
            // Revoke after opening to free memory
            setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
        } catch (e) {
            console.error('Failed to open file preview:', e);
            setAttachmentError('Could not open file preview.');
        }
    };

    const handleUploadAttachments = async () => {
        if (attachments.length === 0) return;

        setUploadingAttachments(true);
        setAttachmentError('');
        setAttachmentSuccess('');
        setUploadSuccess(false);
        setUploadProgress(0);

        try {
            // Simulate smoother progress by updating more frequently
            const totalSteps = attachments.length * 10; // 10 steps per file for smoother animation
            let currentStep = 0;

            // Upload files one by one to track progress
            for (let i = 0; i < attachments.length; i++) {
                // Simulate progress within each file upload
                for (let step = 0; step < 10; step++) {
                    currentStep++;
                    const progress = (currentStep / totalSteps) * 30;
                    setUploadProgress(progress);

                    // Small delay to show smooth progress
                    await new Promise(resolve => setTimeout(resolve, 30));
                }

                // Upload single file
                await uploadOrderAttachments(order.id, [attachments[i]]);
            }

            // Ensure we reach 100% at the end
            setUploadProgress(100);
            await new Promise(resolve => setTimeout(resolve, 300));

            setUploadSuccess(true);
            setAttachments([]); // Clear attachments after successful upload
            // Refresh existing attachments from server
            fetchExistingAttachments();
            // Show success message briefly
            setTimeout(() => setUploadSuccess(false), 3000);
        } catch (error) {
            console.error('Failed to upload attachments:', error);
            setAttachmentError(`Upload failed: ${error.message}`);
        } finally {
            setUploadingAttachments(false);
            setUploadProgress(0);
        }
    };

    const fetchExistingAttachments = async () => {
        if (!order?.id) return;
        try {
            setExistingAttachmentsLoading(true);
            const data = await getOrderAttachments(order.id);
            setExistingAttachments(Array.isArray(data) ? data : []);
        } catch (e) {
            // Silent fail; we avoid blocking the UI
            console.error('Failed to load attachments:', e);
        } finally {
            setExistingAttachmentsLoading(false);
        }
    };

    useEffect(() => {
        fetchExistingAttachments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.id]);

    // Centralized scratch order editing handlers
    const handleSplitScratchOrder = (scratchOrder) => {
        setSelectedScratchOrderForSplit(scratchOrder);
        setShowSplitModal(true);
    };

    const handleCloseSplitModal = () => {
        setShowSplitModal(false);
        setSelectedScratchOrderForSplit(null);
    };

    const handleSplitOrder = async (orderLineId, splits) => {
        setSplittingOrder(true);
        setScratchOrdersError('');
        setScratchOrdersSuccess('');

        try {
            await splitScratchOrder(orderLineId, splits);
            setScratchOrdersSuccess('Scratch order split successfully!');

            // Refresh the order data to show the new split orders
            await refreshOrderData();

            // Close the modal
            setShowSplitModal(false);
            setSelectedScratchOrderForSplit(null);

            // Clear success message after a short delay
            setTimeout(() => setScratchOrdersSuccess(''), 3000);
        } catch (error) {
            console.error('Failed to split scratch order:', error);
            setScratchOrdersError(`Failed to split: ${error.message}`);
        } finally {
            setSplittingOrder(false);
        }
    };

    // Function to refresh the current order data
    const refreshOrderData = async () => {
        if (!order?.id) return;

        setRefreshingOrder(true);
        try {
            const refreshedOrder = await getManagementOrderById(order.id, 'ALL');
            // Update the order prop by calling the parent's update function
            if (onOrderUpdate) {
                // We'll pass a special flag to indicate this is a refresh, not a status update
                onOrderUpdate(order.id, null, { refresh: true, order: refreshedOrder });
            }
        } catch (error) {
            console.error('Failed to refresh order data:', error);
            setScratchOrdersError('Failed to refresh order data. Please refresh the page manually.');
        } finally {
            setRefreshingOrder(false);
        }
    };

    const handleSaveScratchOrder = async (scratchOrderId, itemEdits) => {
        setUpdatingScratchOrders(true);
        setScratchOrdersError('');
        setScratchOrdersSuccess('');

        try {
            const original = order.scratchCardOrders.find(item => String(item.id) === String(scratchOrderId));
            if (!original) {
                console.error(`Scratch card order with ID ${scratchOrderId} not found in order.scratchCardOrders`);
                setScratchOrdersError(`Error: Scratch order details not found.`);
                setSelectedScratchOrderForEdit(null);
                return;
            }

            const originalFrom = original.from !== undefined && original.from !== null ? String(original.from).trim() : '';
            const originalTo = original.to !== undefined && original.to !== null ? String(original.to).trim() : '';
            const originalSubStatus = original.subStatus !== undefined && original.subStatus !== null ? String(original.subStatus).trim() : '';

            const editFrom = itemEdits.from !== undefined && itemEdits.from !== null ? String(itemEdits.from).trim() : '';
            const editTo = itemEdits.to !== undefined && itemEdits.to !== null ? String(itemEdits.to).trim() : '';
            const editSubStatus = itemEdits.subStatus !== undefined && itemEdits.subStatus !== null ? String(itemEdits.subStatus).trim() : '';

            // Check if there are actual changes by comparing with original values
            const hasChanges = (
                editFrom !== originalFrom ||
                editTo !== originalTo ||
                (!isEmployee && editSubStatus !== originalSubStatus)
            );

            if (hasChanges) {
                const updateData = {};
                
                if (editFrom) {
                    updateData.from = editFrom;
                } else {
                    updateData.from = null;
                }
                
                if (editTo) {
                    updateData.to = editTo;
                } else {
                    updateData.to = null;
                }
                
                if (!isEmployee) {
                    if (editSubStatus) {
                        updateData.subStatus = editSubStatus;
                    } else {
                        updateData.subStatus = null;
                    }
                }

                const parsedId = isNaN(parseInt(scratchOrderId)) ? scratchOrderId : parseInt(scratchOrderId);
                await updateScratchOrder(parsedId, updateData);
                setScratchOrdersSuccess(`Successfully updated scratch order!`);
                await refreshOrderData();
                setTimeout(() => {
                    setScratchOrdersSuccess('');
                }, 3000);
            }
            setSelectedScratchOrderForEdit(null);
        } catch (error) {
            console.error('Failed to update scratch order:', error);
            setScratchOrdersError(`Failed to update: ${error.message}`);
        } finally {
            setUpdatingScratchOrders(false);
        }
    };

    // Edit modal state
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editError, setEditError] = useState('');
    const [activeTab, setActiveTab] = useState('ePin'); // 'ePin' | 'scratch' | 'eTopUp'
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Denominations and warehouse lists (same as OrderRequest)
    const scratchWarehouses = [
        'Erbil Warehouse',
        'Dohuk Warehouse',
        'Suly Warehouse',
        'Kirkuk Warehouse',
        'Mosul Warehouse',
        'Baghdad Warehouse',
    ];
    const scratchDenominations = [
        { label: '3,000', value: 3300 },
        { label: '5,000', value: 5150 },
        { label: '10,000', value: 10250 },
        { label: '15,000', value: 15300 },
        { label: '30,000', value: 30000 },
        { label: '60,000', value: 60000 },
    ];
    const denominations = {
        ePin: [
            { label: '1,000', value: 1300 },
            { label: '2,000', value: 2375 },
            { label: '3,000', value: 3300 },
            { label: '4,000', value: 4300 },
            { label: '5,000', value: 5125 },
            { label: '6,000', value: 6125 },
            { label: '10,000', value: 10200 },
            { label: '12,000', value: 12240 },
            { label: '15,000', value: 15250 },
            { label: '18,000', value: 18250 },
            { label: '30,000', value: 30000 },
            { label: '60,000', value: 60000 },
        ],
    };
    const warehouseCodeMap = {
        'Erbil Warehouse': 'EBL_WH',
        'Dohuk Warehouse': 'DUH_WH',
        'Suly Warehouse': 'SUL_WH',
        'Kirkuk Warehouse': 'KRK_WH',
        'Mosul Warehouse': 'MSL_WH',
        'Baghdad Warehouse': 'BGD_WH',
    };
    const warehouseDisplayMap = Object.fromEntries(Object.entries(warehouseCodeMap).map(([name, code]) => [code, name]));

    // Edit structures (full matrix like OrderRequest)
    const [scratchSelections, setScratchSelections] = useState({}); // { denomLabel: { warehouseName: qty } }
    const [epinSelections, setEpinSelections] = useState({}); // { clientId: { denomLabel: qty } }
    const [clientContactInfo, setClientContactInfo] = useState({}); // { clientId: { phoneNumber, email } }
    const [clientNames, setClientNames] = useState({}); // { clientId: name }
    const [etopUpAmount, setEtopUpAmount] = useState('');
    const [etopUpNote, setEtopUpNote] = useState('');

    // Build initial form data when opening modal
    const buildInitialEditState = useMemo(() => {
        return () => {
            // Initialize scratchSelections with all denom/warehouse fields set to 0
            const initialScratch = {};
            scratchDenominations.forEach((d) => {
                initialScratch[d.label] = {};
                scratchWarehouses.forEach((w) => (initialScratch[d.label][w] = 0));
            });
            // Fill existing scratch card quantities
            (order.scratchCardOrders || []).forEach((item) => {
                const denomLabel = Number(item.denomination).toLocaleString('en-US');
                const warehouseName = warehouseDisplayMap[item.warehouse] || item.warehouse;
                if (initialScratch[denomLabel] && warehouseName in initialScratch[denomLabel]) {
                    initialScratch[denomLabel][warehouseName] = Number(item.quantity) || 0;
                }
            });

            // Initialize epin selections for clients present in the order
            const initialEpin = {};
            const initialContacts = {};
            const clientIdsInOrder = Array.from(new Set((order.epinOrders || []).map((e) => String(e.clientId))));
            clientIdsInOrder.forEach((clientId) => {
                initialEpin[clientId] = {};
                denominations.ePin.forEach((d) => (initialEpin[clientId][d.label] = 0));
                const first = (order.epinOrders || []).find((e) => String(e.clientId) === clientId);
                initialContacts[clientId] = {
                    phoneNumber: first?.phoneNumber || '',
                    email: first?.email || '',
                };
            });
            // Fill existing epin quantities
            (order.epinOrders || []).forEach((item) => {
                const clientId = String(item.clientId);
                const denomLabel = Number(item.denomination).toLocaleString('en-US');
                if (initialEpin[clientId] && denomLabel in initialEpin[clientId]) {
                    initialEpin[clientId][denomLabel] = Number(item.quantity) || 0;
                }
            });

            // E-TopUp - handle both new eTopUp and old etopUpOrder structures
            const initialTopUp = (order.eTopUp?.amount || order.etopUpOrder?.amount) ? String(order.eTopUp?.amount || order.etopUpOrder?.amount) : '';
            const initialNote = (order.eTopUp?.note || order.etopUpOrder?.note || order.note) || '';

            return { initialScratch, initialEpin, initialContacts, initialTopUp, initialNote };
        };
    }, [order]);

    useEffect(() => {
        if (!order.merchantName && order.wholesalerId) {
            setMerchantLoading(true);
            setMerchantError('');
            getWholesalerById(order.wholesalerId)
                .then(data => setMerchantName(data.merchantName || ''))
                .catch(() => setMerchantError('Could not fetch wholesaler name'))
                .finally(() => setMerchantLoading(false));
        } else if (order.merchantName) {
            setMerchantName(order.merchantName);
        }
    }, [order.merchantName, order.wholesalerId]);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                setClientsLoading(true);
                const data = await getClients();
                setClients(data);
            } catch {
                setClients([]);
            } finally {
                setClientsLoading(false);
            }
        };
        fetchClients();
    }, [accessToken]);

    const orderClientId = order?.clientId;
    // Fetch client name if clientId is present at the top level
    useEffect(() => {
        if (orderClientId) {
            setClientNameLoading(true);
            setClientNameError('');
            getClientById(orderClientId)
                .then(data => setClientName(data.name || ''))
                .catch(() => setClientNameError('Could not fetch client name'))
                .finally(() => setClientNameLoading(false));
        } else {
            setClientName('');
        }
    }, [orderClientId]);

    const handleAction = async (action) => {
        if (!order || !order.id) return;

        setActionLoading(true);
        setActionError('');



        try {
            let status;
            if (action === 'approve') {
                if (order.status.toLowerCase() === 'pending') {
                    status = 'APPROVED1';
                } else if (order.status.toLowerCase() === 'approved1') {
                    status = 'APPROVED2';
                } else {
                    setActionError('Invalid status for approval');
                    return;
                }
            } else if (action === 'reject') {
                // Reject can be done from any status (PENDING, APPROVED1, etc.)
                status = 'REJECTED';
            } else if (action === 'finalize') {
                // Finalize can only be done from APPROVED2 status
                if (order.status.toLowerCase() === 'approved2') {
                    status = 'COMPLETED';
                } else {
                    setActionError('Invalid status for finalization');
                    return;
                }
            } else {
                setActionError('Invalid action');
                return;
            }

            await updateManagementOrderStatus(order.id, status);

            // Call the callback to update the parent component with success data
            if (onOrderUpdate) {
                onOrderUpdate(order.id, status, { orderId: order.id, action: action, status: status });
            }

        } catch (error) {
            console.error('Action failed:', error);

            // Handle specific error cases
            if (error.message.includes('Access denied') || error.message.includes('403')) {
                setActionError(`Access denied. You may not have permission to ${action} orders. Please contact your administrator.`);
            } else {
                setActionError(`Failed to ${action} order: ${error.message}`);
            }
        } finally {
            setActionLoading(false);
        }
    };

    const handleEdit = () => {
        setEditError('');
        const { initialScratch, initialEpin, initialContacts, initialTopUp, initialNote } = buildInitialEditState();
        setScratchSelections(initialScratch);
        setEpinSelections(initialEpin);
        setClientContactInfo(initialContacts);
        setEtopUpAmount(initialTopUp);
        setEtopUpNote(initialNote);
        setActiveTab('ePin');
        setIsEditOpen(true);
    };

    // Ensure client names are loaded similar to OrderDetailsView behavior
    useEffect(() => {
        if (!isEditOpen) return;
        const ids = Object.keys(epinSelections || {});
        ids.forEach((clientId) => {
            const inList = clients.find((c) => String(c.id) === String(clientId));
            if (!inList && !clientNames[String(clientId)]) {
                getClientById(clientId)
                    .then((data) => {
                        setClientNames((prev) => ({ ...prev, [String(clientId)]: data?.name || String(clientId) }));
                    })
                    .catch(() => {
                        setClientNames((prev) => ({ ...prev, [String(clientId)]: String(clientId) }));
                    });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditOpen, epinSelections, clients]);

    const updateScratchQty = (denomLabel, warehouseName, value) => {
        const qty = Math.max(0, parseInt(String(value).replace(/,/g, '')) || 0);
        setScratchSelections((prev) => ({
            ...prev,
            [denomLabel]: {
                ...prev[denomLabel],
                [warehouseName]: qty,
            },
        }));
    };

    const updateEpinQty = (clientId, denomLabel, value) => {
        const qty = Math.max(0, parseInt(String(value).replace(/,/g, '')) || 0);
        setEpinSelections((prev) => ({
            ...prev,
            [clientId]: {
                ...prev[clientId],
                [denomLabel]: qty,
            },
        }));
    };

    const handleConfirmEdit = async () => {
        try {
            setEditSubmitting(true);
            setEditError('');
            // Build scratchCards
            const scratchCards = [];
            Object.entries(scratchSelections).forEach(([denomLabel, warehouseObj]) => {
                const denom = parseInt(denomLabel.replace(/,/g, '')) || 0;
                Object.entries(warehouseObj).forEach(([warehouseName, qty]) => {
                    const quantity = Number(qty) || 0;
                    if (quantity > 0) {
                        scratchCards.push({
                            warehouse: warehouseCodeMap[warehouseName] || warehouseName,
                            denomination: denom,
                            quantity,
                        });
                    }
                });
            });

            // Build epins
            const epins = [];
            Object.entries(epinSelections).forEach(([clientId, denomObj]) => {
                const contact = clientContactInfo[clientId] || {};
                const clientFallback = clients.find((c) => String(c.id) === String(clientId)) || {};
                const phoneNumber = contact.phoneNumber || clientFallback.phoneNumber || clientFallback.phone || '';
                const email = contact.email || clientFallback.email || '';
                Object.entries(denomObj).forEach(([denomLabel, qty]) => {
                    const quantity = Number(qty) || 0;
                    if (quantity > 0) {
                        epins.push({
                            clientId,
                            phoneNumber,
                            email,
                            denomination: parseInt(denomLabel.replace(/,/g, '')) || 0,
                            quantity,
                        });
                    }
                });
            });

            const payload = {
                scratchCards,
                epins,
            };
            if ((Number(etopUpAmount) || 0) > 0) {
                const cleanAmount = String(Number(etopUpAmount) || 0);
                const cleanNote = sanitizeNote(etopUpNote) || null;
                payload.eTopUp = {
                    amount: cleanAmount,
                    note: cleanNote,
                };
                // Backward-compatible legacy fields
                payload.etopUpAmount = Number(etopUpAmount) || 0;
                if (cleanNote) payload.note = cleanNote;
            }
            await updateOrderDetails(order.id, payload);
            setIsEditOpen(false);
            // Redirect to main page with success parameters
            window.location.href = '/management/pending-orders?updated=true&orderId=' + order.id;
        } catch (e) {
            setEditError(e.message || 'Failed to update order');
        } finally {
            setEditSubmitting(false);
        }
    };

    if (!order) return <div className="text-red-600 font-bold">No order data provided to details view.</div>;
    let epinByClient = {};
    if (order.epinOrders && order.epinOrders.length > 0) {
        order.epinOrders.forEach((epin) => {
            if (!epinByClient[epin.clientId]) epinByClient[epin.clientId] = [];
            epinByClient[epin.clientId].push(epin);
        });
    }

    return (
        <>
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-sm border border-gray-200 max-w-6xl mx-auto">
                <h2 className="text-2xl font-bold mb-6">Order Details</h2>
                <div className="mb-4 flex flex-col gap-2">
                    <div><span className="font-semibold">Order ID:</span> {order.id}</div>
                    <div>
                        <span className="font-semibold">Status:</span> <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
                    </div>
                    {(order.assignedDealer || order.dealerName) && (
                        <div><span className="font-semibold">Dealer Name:</span> {order.assignedDealer || order.dealerName}</div>
                    )}
                    <div><span className="font-semibold">Created:</span> {formatDateTime(order.createdAt)}</div>
                    {order.clientId && (
                        <div>
                            <span className="font-semibold">Client ID:</span> {order.clientId}
                            {clientNameLoading ? (
                                <span className="ml-2 text-gray-500">(Loading name...)</span>
                            ) : clientName ? (
                                <span className="ml-2 text-blue-800 font-semibold">{clientName}</span>
                            ) : clientNameError ? (
                                <span className="ml-2 text-red-500">({clientNameError})</span>
                            ) : null}
                        </div>
                    )}
                    {order.wholesalerId && (
                        <>
                            <div><span className="font-semibold">Wholesaler ID:</span> {order.wholesalerId}</div>
                            {(merchantLoading ? (
                                <div className="text-sm text-gray-500">Loading wholesaler name...</div>
                            ) : merchantName ? (
                                <div><span className="font-semibold">Wholesaler Name:</span> {merchantName}</div>
                            ) : merchantError ? (
                                <div className="text-sm text-red-500">{merchantError}</div>
                            ) : null)}
                        </>
                    )}
                    {!order.wholesalerId && merchantName && (
                        <div><span className="font-semibold">Wholesaler Name:</span> {merchantName}</div>
                    )}
                </div>

                {/* Scratch Card Orders */}
                {order.scratchCardOrders && order.scratchCardOrders.length > 0 && (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold mb-2">Scratch Card Orders</h3>
                        <div className="mb-2 text-gray-700">Total: <span className="font-bold">IQD {formatNumber(Math.round(order.scratchAmountToBePaid))}</span></div>

                        {/* Success/Error messages for centralized scratch order updates */}
                        {scratchOrdersSuccess && (
                            <div className="mb-3 p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-medium">{scratchOrdersSuccess}</span>
                                </div>
                            </div>
                        )}
                        {scratchOrdersError && (
                            <div className="mb-3 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-start">
                                    <svg className="w-4 h-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <p className="font-medium">Update Error</p>
                                        <p className="text-red-600 mt-1">{scratchOrdersError}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="overflow-x-auto w-full border border-gray-200 rounded-lg mb-2 shadow-sm">
                            <table className="w-full min-w-[900px] text-sm divide-y divide-gray-200">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Warehouse</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Denomination</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">From</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">To</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sub Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                                        {pageType === 'scratch' && (
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {order.scratchCardOrders
                                        .sort((a, b) => a.denomination - b.denomination)
                                        .map((item, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3 whitespace-nowrap text-gray-700">{warehouseMap[item.warehouse] || item.warehouse}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 font-medium tabular-nums">IQD {formatNumber(item.denomination)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">IQD {formatNumber(item.denominationPrice)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">{item.quantity}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">{item.from || '-'}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">{item.to || '-'}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">
                                                    {item.subStatus ? (
                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSubStatusColor(item.subStatus)}`}>
                                                            {item.subStatus}
                                                        </span>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs font-semibold text-gray-900 tabular-nums">IQD {formatNumber(item.paidAmount)}</td>
                                                {pageType === 'scratch' && (
                                                    <td className="px-4 py-3 whitespace-nowrap flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleSplitScratchOrder(item)}
                                                            disabled={splittingOrder}
                                                            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                                        >
                                                            Split
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setScratchOrdersError('');
                                                                setSelectedScratchOrderForEdit(item);
                                                            }}
                                                            disabled={refreshingOrder}
                                                            className="px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded hover:bg-amber-600 transition-colors disabled:opacity-50 shadow-sm"
                                                        >
                                                            Edit
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                        {refreshingOrder && (
                            <div className="mt-4 flex gap-4">
                                <div className="flex items-center text-sm text-gray-600">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                    Refreshing...
                                </div>
                            </div>
                        )}
                    </div>
                )}


                {/* E-PIN Orders by Client */}
                {order.epinOrders && order.epinOrders.length > 0 && (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold mb-2">E-PIN Orders</h3>
                        <div className="mb-2 text-gray-700">Total: <span className="font-bold">IQD {formatNumber(Math.round(order.epinAmountToBePaid))}</span></div>
                        {clientsLoading ? (
                            <div className="text-gray-500 text-sm mb-2">Loading clients...</div>
                        ) : (
                            Object.entries(epinByClient).map(([clientId, epins]) => (
                                <EpinClientGroup key={clientId} clientId={clientId} epins={epins} clients={clients} />
                            ))
                        )}
                    </div>
                )}

                {/* E-TopUp Order */}
                {(() => {
                    // Handle both new eTopUp structure and old etopUpOrder structure
                    const eTopUpData = order.eTopUp || order.etopUpOrder;
                    const hasETopUp = (eTopUpData && eTopUpData.amount) || order.etopUpAmount;

                    if (!hasETopUp) return null;

                    return (
                        <div className="mb-8">
                            <h3 className="text-lg font-semibold mb-2">E-TopUp Order</h3>
                            <div className="mb-2 text-gray-700">Total: <span className="font-bold">IQD {formatNumber(order.etopUpAmountToBePaid || ((eTopUpData?.amount || order.etopUpAmount) * 1.025))}</span></div>
                            <div className="overflow-x-auto w-full border border-gray-200 rounded-lg mb-2 shadow-sm">
                                <table className="w-full text-sm divide-y divide-gray-200" style={{ tableLayout: 'fixed', minWidth: '600px' }}>
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>Amount</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ width: '50%' }}>Note</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>Calculated Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 text-xs text-gray-900 font-medium tabular-nums">IQD {formatNumber(eTopUpData?.amount || order.etopUpAmount)}</td>
                                            <td className="px-4 py-3 text-gray-700" style={{ wordBreak: 'break-word', whiteSpace: 'normal', overflowWrap: 'break-word' }}>{(eTopUpData?.note ?? order.note) || '—'}</td>
                                            <td className="px-4 py-3 text-xs text-gray-900 font-semibold tabular-nums">IQD {formatNumber(eTopUpData?.paidAmount)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })()}

                {/* Grand Total */}
                <div className="mt-8 flex justify-end">
                    {order.discountDetails && order.discountDetails.paidAmountAfterDiscount != null ? (
                        <div className="flex flex-col items-end">
                            <div className="text-xl font-bold text-gray-900 mb-1">
                                Grand Total
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-gray-400 line-through text-lg">
                                    IQD {formatNumber(order.discountDetails.paidPrice)}
                                </span>
                                {(Number(order.discountDetails.discountByPercentage) > 0 || Number(order.discountDetails.discountByAmount) > 0) && (
                                    <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-100">
                                        -{Number(order.discountDetails.discountByPercentage) > 0
                                            ? `${Number(order.discountDetails.discountByPercentage)}%`
                                            : `IQD ${formatNumber(order.discountDetails.discountByAmount)}`}
                                    </span>
                                )}
                            </div>
                            <div className="text-2xl font-bold text-gray-900">
                                IQD {formatNumber(order.discountDetails.paidAmountAfterDiscount)}
                            </div>
                        </div>
                    ) : (
                        <div className="text-xl font-bold text-right text-gray-900">
                            Grand Total: {calculateOrderTotal(order) !== null ? `IQD ${formatNumber(calculateOrderTotal(order))}` : 'N/A'}
                        </div>
                    )}
                </div>

                {/* Existing Attachments (Management view) */}
                <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-3">Existing Attachments</h3>
                    {existingAttachmentsLoading ? (
                        <div className="text-sm text-gray-600">Loading attachments...</div>
                    ) : existingAttachments.length === 0 ? (
                        <div className="text-sm text-gray-600">No attachments available.</div>
                    ) : (
                        <ul className="divide-y divide-gray-200 bg-white border border-gray-200 rounded-md">
                            {existingAttachments.map((att) => (
                                <li key={att.id} className="flex items-center justify-between px-3 py-2">
                                    <div className="text-sm text-gray-800 truncate mr-3">
                                        <span className="text-gray-600 mr-2">#{att.id}</span>
                                        <span className="uppercase text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">{att.contentType?.split('/')[1] || att.contentType || 'FILE'}</span>
                                        <span className="ml-2 text-xs text-gray-500">({att.size || '—'})</span>
                                    </div>
                                    <div>
                                        <a
                                            href={`${process.env.REACT_APP_API_URL}${att.link.replace('/api', '')}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-700 text-sm"
                                        >
                                            View
                                        </a>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Attachments - only for APPROVED1 and APPROVED2 */}
                {(order.status === 'APPROVED1' || order.status === 'APPROVED2') && (
                    <div
                        className={`mt-8 p-4 border rounded-lg ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50'
                            }`}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragOver(true);
                        }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragOver(false);
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragOver(false);
                            handleFilesAdd(e.dataTransfer.files);
                        }}
                    >
                        <h3 className="text-lg font-semibold mb-3">Invoice Attachments</h3>
                        <p className="text-sm text-gray-600 mb-3">Upload PNG, JPEG/JPG, PDF, or ZIP files related to this order. Maximum file size: 20 MB.</p>

                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    fileInputRef.current?.click();
                                }
                            }}
                            className={`w-full flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-white'
                                } cursor-pointer`}
                        >
                            <div className="flex items-center gap-2">
                                <span className="inline-flex items-center px-3 py-2 rounded-md bg-blue-100 text-blue-700 text-sm font-semibold hover:bg-blue-200">
                                    Choose files
                                </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                Supported formats: PNG, JPEG, PDF, ZIP • Max size: 20 MB per file
                            </div>
                            <div className="text-xs text-blue-600 mt-1 font-medium">
                                Click or drag files here to upload
                            </div>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,application/pdf,application/zip,application/x-zip-compressed,.zip"
                            multiple
                            className="hidden"
                            title="Maximum file size: 20 MB per file. Supported formats: PNG, JPEG, PDF, ZIP"
                            onChange={(e) => {
                                handleFilesAdd(e.target.files);
                                if (e.target) e.target.value = '';
                            }}
                        />
                        {attachmentError && (
                            <div className="mt-2 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-start">
                                    <svg className="w-4 h-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <p className="font-medium">Upload Error</p>
                                        <p className="text-red-600 mt-1">{attachmentError}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {processingFiles && (
                            <div className="mt-2 p-3 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="flex items-center">
                                    <svg className="animate-spin w-4 h-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className="font-medium">Processing files...</span>
                                </div>
                            </div>
                        )}
                        {attachmentSuccess && (
                            <div className="mt-2 p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-medium">{attachmentSuccess}</span>
                                </div>
                            </div>
                        )}
                        {uploadSuccess && (
                            <div className="mt-2 p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-medium">Attachments uploaded successfully!</span>
                                </div>
                            </div>
                        )}

                        {attachments.length > 0 && (
                            <div className="mt-3">
                                <ul className="divide-y divide-gray-200 bg-white border border-gray-200 rounded-md">
                                    {attachments.map((file, idx) => (
                                        <li key={idx} className="flex items-center justify-between px-3 py-2">
                                            <div className="text-sm text-gray-800 truncate mr-3">
                                                {file.name}
                                                <span className="ml-2 text-xs text-gray-500">({formatFileSize(file.size)})</span>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <button
                                                    className="text-blue-600 hover:text-blue-700 text-sm"
                                                    onClick={() => handleViewFile(file)}
                                                >
                                                    View
                                                </button>
                                                <button
                                                    className="text-red-600 hover:text-red-700 text-sm"
                                                    onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                {uploadingAttachments && (
                                    <div className="mt-3 mb-3">
                                        <div className="text-sm text-gray-600 mb-2">Uploading files...</div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                                <div className="mt-3 flex justify-end">
                                    <button
                                        className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                        disabled={attachments.length === 0 || uploadingAttachments || processingFiles}
                                        onClick={handleUploadAttachments}
                                        title={processingFiles ? 'Please wait while files are being processed...' : ''}
                                    >
                                        {uploadingAttachments ? 'Uploading...' : 'Upload'}
                                    </button>
                                    {processingFiles && (
                                        <div className="ml-3 text-xs text-gray-500 flex items-center">
                                            <svg className="animate-spin w-3 h-3 text-gray-400 mr-1" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Processing...
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                {order.status !== 'COMPLETED' && order.status !== 'REJECTED' && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <div>
                                {order.status !== 'APPROVED2' && (
                                    <button
                                        onClick={handleEdit}
                                        disabled={actionLoading}
                                        className={`px-6 py-2 border-2 border-gray-300 rounded-md text-sm font-medium transition-colors duration-200 ${actionLoading
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-white text-black hover:bg-gray-100'
                                            }`}
                                    >
                                        Edit
                                    </button>
                                )}
                            </div>

                            <div className="flex space-x-4">
                                {order.status === 'APPROVED2' ? (
                                    // Show Finalize Order button for APPROVED2 status
                                    <button
                                        onClick={() => handleAction('finalize')}
                                        disabled={actionLoading}
                                        className={`px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${actionLoading
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        {actionLoading ? 'Processing...' : 'Finalize Order'}
                                    </button>
                                ) : (
                                    // Show Approve/Reject buttons for other statuses
                                    <>
                                        <button
                                            onClick={() => handleAction('approve')}
                                            disabled={actionLoading || (order.status !== 'PENDING' && order.status !== 'APPROVED1')}
                                            className={`px-4 py-2 rounded font-medium ${actionLoading || (order.status !== 'PENDING' && order.status !== 'APPROVED1')
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-green-600 hover:bg-green-700 text-white'
                                                }`}
                                        >
                                            {actionLoading ? 'Processing...' : 'Approve'}
                                        </button>

                                        <button
                                            onClick={() => handleAction('reject')}
                                            disabled={actionLoading || (order.status !== 'PENDING' && order.status !== 'APPROVED1')}
                                            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${actionLoading || (order.status !== 'PENDING' && order.status !== 'APPROVED1')
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-red-600 text-white hover:bg-red-700'
                                                }`}
                                        >
                                            {actionLoading ? 'Processing...' : 'Reject'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {actionError && (
                            <div className="mt-4 text-center text-red-600 text-sm">
                                {actionError}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* Edit Modal */}
            <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} size="xl" className="" hideCloseButton>
                <div className="max-h-[80vh] overflow-y-auto">
                    <div className="mb-4 flex items-start justify-between">
                        <div>
                            <h3 className="text-xl font-semibold">Edit Order</h3>
                            <p className="text-sm text-gray-600">Update item quantities across all available denominations.</p>
                        </div>
                        <div className="text-sm text-gray-600">
                            Order ID: <span className="font-semibold text-gray-900">{order?.id}</span>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex space-x-2 mb-4">
                        {['ePin', 'scratch', 'eTopUp'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-3 py-2 rounded border text-sm ${activeTab === tab ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
                            >
                                {tab === 'ePin' ? 'E-PIN' : tab === 'scratch' ? 'Scratch Cards' : 'E-TopUp'}
                            </button>
                        ))}
                    </div>

                    {/* E-PIN Tab */}
                    {activeTab === 'ePin' && (
                        <div className="space-y-6">
                            {Object.keys(epinSelections).length === 0 ? (
                                <div className="text-sm text-gray-500">No E-PIN clients in this order.</div>
                            ) : (
                                Object.entries(epinSelections).map(([clientId, denomMap]) => {
                                    const client = clients.find((c) => String(c.id) === String(clientId));
                                    const displayName = client ? client.name : (clientNames[String(clientId)] || 'Unknown Client');
                                    return (
                                        <div key={clientId} className="border rounded-lg p-4">
                                            <div className="mb-3">
                                                <div className="text-sm text-gray-600">Client</div>
                                                <div className="font-bold text-blue-800">{displayName}</div>
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                                {denominations.ePin.map((d) => (
                                                    <div key={d.label} className="flex items-center justify-between gap-2 border rounded p-2">
                                                        <div className="text-sm font-bold">{d.label}</div>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            className="w-24 px-2 py-1 border border-gray-400 rounded"
                                                            value={denomMap[d.label] > 0 ? denomMap[d.label] : ""}
                                                            onChange={(e) => updateEpinQty(clientId, d.label, e.target.value)}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {/* Scratch Cards Tab */}
                    {activeTab === 'scratch' && (
                        <div className="space-y-4">
                            <div className="overflow-x-auto w-full border border-gray-200 rounded-lg shadow-sm">
                                <table className="w-full min-w-[800px] text-sm divide-y divide-gray-200">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Denomination</th>
                                            {scratchWarehouses.map((w) => (
                                                <th key={w} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{w}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {scratchDenominations.map((d) => (
                                            <tr key={d.label} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3 whitespace-nowrap text-gray-900 font-bold">{d.label}</td>
                                                {scratchWarehouses.map((w) => (
                                                    <td key={`${d.label}-${w}`} className="px-4 py-3 whitespace-nowrap">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            className="w-24 px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                                                            value={scratchSelections[d.label]?.[w] > 0 ? scratchSelections[d.label]?.[w] : ""}
                                                            onChange={(e) => updateScratchQty(d.label, w, e.target.value)}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* E-TopUp Tab */}
                    {activeTab === 'eTopUp' && (
                        <div className="mb-6 space-y-4">
                            <div className="flex items-center space-x-3">
                                <label className="text-sm text-gray-700">Amount</label>
                                <input
                                    type="number"
                                    min="0"
                                    className="w-48 px-3 py-2 border border-gray-400 rounded"
                                    value={etopUpAmount}
                                    onChange={(e) => setEtopUpAmount(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Note (optional, max 250 characters)</label>
                                <textarea
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                                    rows={3}
                                    maxLength={250}
                                    value={etopUpNote}
                                    onChange={(e) => setEtopUpNote(sanitizeNote(e.target.value))}
                                    placeholder="Add an optional note about this E-TopUp"
                                />
                                <div className="mt-1 text-xs text-gray-500">{etopUpNote.length}/250</div>
                            </div>
                            <div className="text-xs text-gray-500">Enter the total e-TopUp amount.</div>
                        </div>
                    )}

                    {editError && (
                        <div className="mb-4 text-red-600 text-sm">{editError}</div>
                    )}

                    {/* Sticky footer actions */}
                    <div className="sticky bottom-0 left-0 right-0 bg-white pt-4 mt-6 border-t flex justify-end gap-3">
                        <button
                            className="px-4 py-2 rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                            onClick={() => setIsEditOpen(false)}
                            disabled={editSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            className={`px-5 py-2 rounded ${editSubmitting ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                            onClick={handleConfirmEdit}
                            disabled={editSubmitting}
                        >
                            {editSubmitting ? 'Saving...' : 'Confirm'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Success Modal */}
            <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} hideCloseButton>
                <div className="text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                        <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Order Updated Successfully!
                    </h3>
                    <div className="text-sm text-gray-600 mb-4">
                        <p>Order ID: <strong>{order.id}</strong></p>
                        <p>The order quantities have been updated.</p>
                    </div>
                    <div className="mt-6">
                        <button
                            onClick={() => setShowSuccessModal(false)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            OK
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Split Modal */}
            <SplitModal
                isOpen={showSplitModal}
                onClose={handleCloseSplitModal}
                scratchOrder={selectedScratchOrderForSplit}
                onSplit={handleSplitOrder}
            />

            {/* Edit Scratch Orders Modal */}
            <EditScratchOrdersModal
                isOpen={selectedScratchOrderForEdit !== null}
                onClose={() => setSelectedScratchOrderForEdit(null)}
                scratchCardOrder={selectedScratchOrderForEdit}
                isEmployee={isEmployee}
                onSave={handleSaveScratchOrder}
                isSaving={updatingScratchOrders}
                error={scratchOrdersError}
            />
        </>
    );
};

export default ManagementOrderDetailsView; 